import React, { useState, useRef, useCallback } from 'react';
import { FiUploadCloud, FiFile, FiCheck, FiX, FiLoader, FiAlertCircle } from 'react-icons/fi';
import { sopUploadDocument } from '../../db/company';
const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.doc', '.txt'];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

function getFileStatus(status) {
  switch (status) {
    case 'pending': return { color: '#94A3B8', icon: FiFile, label: 'Pending' };
    case 'processing': return { color: '#3B82F6', icon: FiLoader, label: 'Processing' };
    case 'done': return { color: '#10B981', icon: FiCheck, label: 'Done' };
    case 'error': return { color: '#EF4444', icon: FiAlertCircle, label: 'Error' };
    default: return { color: '#94A3B8', icon: FiFile, label: status };
  }
}

export default function UploadDocuments() {
  const [queue, setQueue] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);
  const processingRef = useRef(false);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const addFiles = useCallback((fileList) => {
    const newItems = [];
    for (const file of fileList) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        showToast(`"${file.name}" is not a supported file type`, 'error');
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        showToast(`"${file.name}" exceeds 50 MB limit`, 'error');
        continue;
      }
      newItems.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        name: file.name,
        size: file.size,
        status: 'pending',
        error: null,
        result: null,
      });
    }
    if (newItems.length > 0) {
      setQueue(prev => [...prev, ...newItems]);
    }
  }, []);

  // Process queue
  React.useEffect(() => {
    const processNext = async () => {
      if (processingRef.current) return;

      setQueue(prev => {
        const pendingIdx = prev.findIndex(item => item.status === 'pending');
        if (pendingIdx === -1) return prev;

        processingRef.current = true;
        const item = prev[pendingIdx];
        const updated = [...prev];
        updated[pendingIdx] = { ...item, status: 'processing' };

        // Fire off upload
        uploadFile(item).then(result => {
          setQueue(p => p.map(q =>
            q.id === item.id ? { ...q, status: 'done', result } : q
          ));
          showToast(`"${item.name}" uploaded — ${result.chunks_created} chunks created`);
          processingRef.current = false;
        }).catch(err => {
          setQueue(p => p.map(q =>
            q.id === item.id ? { ...q, status: 'error', error: err.message } : q
          ));
          showToast(`Failed to upload "${item.name}": ${err.message}`, 'error');
          processingRef.current = false;
        });

        return updated;
      });
    };

    const interval = setInterval(processNext, 500);
    return () => clearInterval(interval);
  }, []);

  const uploadFile = async (item) => {
    const data = await sopUploadDocument(item.file);
    if (data?.error) throw new Error(data.error);
    return data;
  };

  const removeItem = (id) => {
    setQueue(prev => prev.filter(item => item.id !== id));
  };

  const clearCompleted = () => {
    setQueue(prev => prev.filter(item => item.status !== 'done' && item.status !== 'error'));
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (e.dataTransfer.files?.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files?.length > 0) {
      addFiles(e.target.files);
      e.target.value = '';
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const pendingCount = queue.filter(i => i.status === 'pending').length;
  const processingCount = queue.filter(i => i.status === 'processing').length;
  const doneCount = queue.filter(i => i.status === 'done').length;

  return (
    <div style={{ padding: '0 0 40px 0' }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          padding: '12px 20px', borderRadius: 8,
          background: toast.type === 'error' ? '#DC2626' : '#059669',
          color: '#fff', fontSize: 14, fontWeight: 500,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ color: '#F1F5F9', fontSize: 24, fontWeight: 700, margin: 0 }}>
          Upload Documents
        </h1>
        <p style={{ color: '#64748B', fontSize: 14, margin: '4px 0 0' }}>
          Upload local SOP files to be chunked, embedded, and stored in the vector database
        </p>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          padding: '48px 24px',
          borderRadius: 12,
          border: `2px dashed ${dragging ? '#3B82F6' : '#1E2A4A'}`,
          background: dragging ? '#111D33' : '#0A0F1E',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          marginBottom: 24,
        }}
      >
        <FiUploadCloud size={40} style={{
          color: dragging ? '#3B82F6' : '#334155',
          marginBottom: 12,
        }} />
        <div style={{ color: '#E2E8F0', fontSize: 16, fontWeight: 500, marginBottom: 4 }}>
          {dragging ? 'Drop files here' : 'Drag & drop files here'}
        </div>
        <div style={{ color: '#64748B', fontSize: 13 }}>
          or click to browse. Accepts: PDF, DOCX, DOC, TXT (max 50 MB)
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.doc,.txt"
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
      </div>

      {/* Queue */}
      {queue.length > 0 && (
        <>
          {/* Queue header */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 12,
          }}>
            <div style={{ color: '#94A3B8', fontSize: 13 }}>
              {pendingCount > 0 && `${pendingCount} pending`}
              {processingCount > 0 && ` · ${processingCount} processing`}
              {doneCount > 0 && ` · ${doneCount} done`}
            </div>
            {(doneCount > 0 || queue.some(i => i.status === 'error')) && (
              <button
                onClick={clearCompleted}
                style={{
                  background: 'none', border: 'none',
                  color: '#64748B', fontSize: 13, cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Clear completed
              </button>
            )}
          </div>

          {/* File list */}
          <div style={{
            background: '#0F1A2E', borderRadius: 12,
            border: '1px solid #1E2A4A', overflow: 'hidden',
          }}>
            {queue.map(item => {
              const statusInfo = getFileStatus(item.status);
              const StatusIcon = statusInfo.icon;
              return (
                <div key={item.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '14px 16px',
                  borderBottom: '1px solid #1E2A4A',
                }}>
                  <StatusIcon
                    size={18}
                    style={{
                      color: statusInfo.color, flexShrink: 0,
                      animation: item.status === 'processing' ? 'spin 1s linear infinite' : 'none',
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      color: '#E2E8F0', fontSize: 14, fontWeight: 500,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {item.name}
                    </div>
                    {item.error && (
                      <div style={{ color: '#FCA5A5', fontSize: 12, marginTop: 2 }}>{item.error}</div>
                    )}
                    {item.result && (
                      <div style={{ color: '#86EFAC', fontSize: 12, marginTop: 2 }}>
                        {item.result.chunks_created} chunks created
                      </div>
                    )}
                  </div>
                  <span style={{ color: '#475569', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {formatSize(item.size)}
                  </span>
                  <span style={{
                    color: statusInfo.color, fontSize: 12, fontWeight: 500,
                    minWidth: 70, textAlign: 'right',
                  }}>
                    {statusInfo.label}
                  </span>
                  {(item.status === 'done' || item.status === 'error') && (
                    <button
                      onClick={() => removeItem(item.id)}
                      style={{
                        background: 'none', border: 'none',
                        color: '#475569', cursor: 'pointer', padding: 4,
                        display: 'flex',
                      }}
                    >
                      <FiX size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <style>{`
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
}
